import type {
  AuditEntry,
  BulkActionInput,
  DraftInventoryInput,
  DraftPartInput,
  ImageReorderInput,
  Inventory,
  InventoryCreateInput,
  InventoryImageInput,
  InventoryListQuery,
  InventoryUpdateInput,
  Lookup,
  LookupInput,
  Part,
  PartBulkActionInput,
  PartCreateInput,
  PartImageInput,
  PartListQuery,
  PartUpdateInput,
  Product,
  ProductBulkActionInput,
  ProductCreateInput,
  ProductImageInput,
  ProductListQuery,
  ProductUpdateInput,
} from '@ncc/shared';

import { setSessionExpired } from './session';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    // The API never redirects. A redirect means Cloudflare Access is sending us
    // to sign in again; following it cross-origin throws "Failed to fetch", so
    // stop and say what happened instead (see lib/session.ts).
    redirect: 'manual',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (res.type === 'opaqueredirect') {
    setSessionExpired(true);
    throw new SessionExpiredError();
  }
  setSessionExpired(false);
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export class SessionExpiredError extends Error {
  constructor() {
    super('Nothing was saved. Use “Sign in again” at the top, then save again.');
    this.name = 'Signed out';
  }
}

export class ApiError extends Error {
  constructor(public status: number, public detail: unknown) {
    super(`API ${status}${summarizeDetail(detail)}`);
  }
}

// Surface the server's validation issues (or error string) in the message so
// failures read as e.g. "API 400 — tag_id: expected positive" instead of a
// bare status code.
function summarizeDetail(detail: unknown): string {
  if (!detail || typeof detail !== 'object') return '';
  const d = detail as { error?: unknown; issues?: unknown };
  if (Array.isArray(d.issues) && d.issues.length > 0) {
    const parts = (d.issues as Array<{ path?: unknown[]; message?: string }>)
      .slice(0, 5)
      .map((i) => `${(i.path ?? []).join('.') || '(root)'}: ${i.message ?? 'invalid'}`);
    return ` — ${parts.join('; ')}`;
  }
  if (typeof d.error === 'string') {
    // Append the server's detail when the headline is the generic 500 text.
    // Without this the dashboard showed "API 500 — Internal Server Error" and
    // dropped the one useful part of the body: a reused VIN, for instance, threw
    // "UNIQUE constraint failed: inventory.vin" into `detail` where nobody saw
    // it. Those are 409s with a real message now, but an unmapped fault should
    // still arrive with something to go on.
    const extra =
      d.error === 'Internal Server Error' && typeof (d as { detail?: unknown }).detail === 'string'
        ? ` (${(d as { detail: string }).detail})`
        : '';
    return ` — ${d.error}${extra}`;
  }
  return '';
}

type Paged<T> = { items: T[]; total: number; page: number; page_size: number };

function buildQuery(q: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      for (const x of v) params.append(k, String(x));
    } else {
      params.append(k, String(v));
    }
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export const api = {
  // Inventory
  listInventory(q: Partial<InventoryListQuery>) {
    return request<Paged<Inventory>>(`/v1/admin/inventory${buildQuery(q)}`);
  },
  getInventory(id: number) {
    return request<Inventory>(`/v1/admin/inventory/${id}`);
  },
  createInventory(input: InventoryCreateInput) {
    return request<Inventory>('/v1/admin/inventory', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  saveDraft(input: DraftInventoryInput) {
    return request<Inventory>('/v1/admin/inventory/draft', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  /**
   * `deferSync` suppresses the Google Sheet mirror for this call. Use it when a
   * PUT /images call follows immediately (the standard two-step form save):
   * otherwise both calls mirror concurrently and the listing lands in the sheet
   * twice. The trailing call is then responsible for the mirror.
   */
  updateInventory(id: number, input: InventoryUpdateInput, opts?: { deferSync?: boolean }) {
    return request<Inventory>(
      `/v1/admin/inventory/${id}${opts?.deferSync ? '?defer_sync=1' : ''}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    );
  },
  deleteInventory(id: number) {
    return request<{ ok: true }>(`/v1/admin/inventory/${id}`, { method: 'DELETE' });
  },
  bulk(input: BulkActionInput) {
    return request<{ ok: true }>('/v1/admin/inventory/bulk', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // Images
  addImages(id: number, images: InventoryImageInput[]) {
    return request<Inventory>(`/v1/admin/inventory/${id}/images`, {
      method: 'POST',
      body: JSON.stringify({ images }),
    });
  },
  replaceImages(id: number, images: InventoryImageInput[]) {
    return request<Inventory>(`/v1/admin/inventory/${id}/images`, {
      method: 'PUT',
      body: JSON.stringify({ images }),
    });
  },
  reorderImages(id: number, input: ImageReorderInput) {
    return request<Inventory>(`/v1/admin/inventory/${id}/images/reorder`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  removeImage(id: number, imageId: number) {
    return request<{ inventory: Inventory }>(`/v1/admin/inventory/${id}/images/${imageId}`, {
      method: 'DELETE',
    });
  },

  // Lookups
  listBrands() {
    return request<Lookup[]>('/v1/admin/brands');
  },
  createBrand(input: LookupInput) {
    return request<Lookup>('/v1/admin/brands', { method: 'POST', body: JSON.stringify(input) });
  },
  updateBrand(id: number, input: LookupInput) {
    return request<Lookup>(`/v1/admin/brands/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },
  deleteBrand(id: number) {
    return request<{ ok: true }>(`/v1/admin/brands/${id}`, { method: 'DELETE' });
  },
  listInventoryTypes() {
    return request<Lookup[]>('/v1/admin/inventory-types');
  },
  createInventoryType(input: LookupInput) {
    return request<Lookup>('/v1/admin/inventory-types', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  updateInventoryType(id: number, input: LookupInput) {
    return request<Lookup>(`/v1/admin/inventory-types/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },
  deleteInventoryType(id: number) {
    return request<{ ok: true }>(`/v1/admin/inventory-types/${id}`, { method: 'DELETE' });
  },
  listTags() {
    return request<Lookup[]>('/v1/admin/tags');
  },
  createTag(input: LookupInput) {
    return request<Lookup>('/v1/admin/tags', { method: 'POST', body: JSON.stringify(input) });
  },
  updateTag(id: number, input: LookupInput) {
    return request<Lookup>(`/v1/admin/tags/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },
  deleteTag(id: number) {
    return request<{ ok: true }>(`/v1/admin/tags/${id}`, { method: 'DELETE' });
  },
  listPartCategories() {
    return request<Lookup[]>('/v1/admin/part-categories');
  },
  createPartCategory(input: LookupInput) {
    return request<Lookup>('/v1/admin/part-categories', { method: 'POST', body: JSON.stringify(input) });
  },
  updatePartCategory(id: number, input: LookupInput) {
    return request<Lookup>(`/v1/admin/part-categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },
  deletePartCategory(id: number) {
    return request<{ ok: true }>(`/v1/admin/part-categories/${id}`, { method: 'DELETE' });
  },
  listProductCategories() {
    return request<Lookup[]>('/v1/admin/product-categories');
  },
  createProductCategory(input: LookupInput) {
    return request<Lookup>('/v1/admin/product-categories', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  updateProductCategory(id: number, input: LookupInput) {
    return request<Lookup>(`/v1/admin/product-categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },
  deleteProductCategory(id: number) {
    return request<{ ok: true }>(`/v1/admin/product-categories/${id}`, { method: 'DELETE' });
  },

  // Parts
  listParts(q: Partial<PartListQuery>) {
    return request<Paged<Part>>(`/v1/admin/parts${buildQuery(q)}`);
  },
  getPart(id: number) {
    return request<Part>(`/v1/admin/parts/${id}`);
  },
  createPart(input: PartCreateInput) {
    return request<Part>('/v1/admin/parts', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  savePartDraft(input: DraftPartInput) {
    return request<Part>('/v1/admin/parts/draft', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  updatePart(id: number, input: PartUpdateInput) {
    return request<Part>(`/v1/admin/parts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },
  deletePart(id: number) {
    return request<{ ok: true }>(`/v1/admin/parts/${id}`, { method: 'DELETE' });
  },
  bulkParts(input: PartBulkActionInput) {
    return request<{ ok: true }>('/v1/admin/parts/bulk', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  addPartImages(id: number, images: PartImageInput[]) {
    return request<Part>(`/v1/admin/parts/${id}/images`, {
      method: 'POST',
      body: JSON.stringify({ images }),
    });
  },
  replacePartImages(id: number, images: PartImageInput[]) {
    return request<Part>(`/v1/admin/parts/${id}/images`, {
      method: 'PUT',
      body: JSON.stringify({ images }),
    });
  },
  reorderPartImages(id: number, input: ImageReorderInput) {
    return request<Part>(`/v1/admin/parts/${id}/images/reorder`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  removePartImage(id: number, imageId: number) {
    return request<{ part: Part }>(`/v1/admin/parts/${id}/images/${imageId}`, {
      method: 'DELETE',
    });
  },

  // Products
  listProducts(q: Partial<ProductListQuery>) {
    return request<Paged<Product>>(`/v1/admin/products${buildQuery(q)}`);
  },
  getProduct(id: number) {
    return request<Product>(`/v1/admin/products/${id}`);
  },
  createProduct(input: ProductCreateInput) {
    return request<Product>('/v1/admin/products', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  updateProduct(id: number, input: ProductUpdateInput) {
    return request<Product>(`/v1/admin/products/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },
  deleteProduct(id: number) {
    return request<{ ok: true }>(`/v1/admin/products/${id}`, { method: 'DELETE' });
  },
  bulkProducts(input: ProductBulkActionInput) {
    return request<{ ok: true }>('/v1/admin/products/bulk', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  addProductImages(id: number, images: ProductImageInput[]) {
    return request<Product>(`/v1/admin/products/${id}/images`, {
      method: 'POST',
      body: JSON.stringify({ images }),
    });
  },
  replaceProductImages(id: number, images: ProductImageInput[]) {
    return request<Product>(`/v1/admin/products/${id}/images`, {
      method: 'PUT',
      body: JSON.stringify({ images }),
    });
  },
  reorderProductImages(id: number, input: ImageReorderInput) {
    return request<Product>(`/v1/admin/products/${id}/images/reorder`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  removeProductImage(id: number, imageId: number) {
    return request<{ product: Product }>(`/v1/admin/products/${id}/images/${imageId}`, {
      method: 'DELETE',
    });
  },

  // Audit
  listAudit(q: { page?: number; page_size?: number; entity?: string; entity_id?: number }) {
    return request<Paged<AuditEntry>>(`/v1/admin/audit${buildQuery(q)}`);
  },

  // Google Sheet mirror
  sheetStatus() {
    return request<{
      configured: boolean;
      sheet_id: string | null;
      tab: string | null;
      sheet_rows: number | null;
      db_rows: number;
      error?: string | null;
    }>('/v1/admin/sheets/status');
  },
  /** One chunk. The caller loops until `next_offset` comes back null. */
  sheetBackfill(offset = 0) {
    return request<{
      ok: true;
      rows: number;
      total: number;
      next_offset: number | null;
      /** Row count read back from the sheet on the final chunk. */
      sheet_rows: number | null;
      /** False when the sheet ended up with a different row count than the DB. */
      verified: boolean | null;
    }>('/v1/admin/sheets/backfill', { method: 'POST', body: JSON.stringify({ offset }) });
  },

  // Import
  importCsv(rows: unknown[]) {
    return request<{ created: number; updated: number; skipped: number; errors: unknown[] }>(
      '/v1/admin/import/csv',
      { method: 'POST', body: JSON.stringify({ rows }) },
    );
  },
};
